/** «Сколько можно тратить» — ядро продукта (§16.21, ADR-0002).
 *
 * Что показать, решает `limitView` — чистая функция, проверенная тестами.
 * Здесь только вёрстка: так тон разговора о деньгах нельзя случайно изменить,
 * поправив отступ.
 */
import type { Allowance } from "../lib/api.ts";
import { limitView, type Tone } from "../lib/limit.ts";
import { card } from "./ui.tsx";

const INK: Record<Tone, string> = {
  normal: "var(--text)",
  near: "var(--warn)",
  over: "var(--danger)",
};

export function LimitCard({ state, onSet, corner }: {
  state: Allowance | null;
  onSet: () => void;
  /** Подвал героя — там живёт капитал (MA-FR-2). Не верхний угол: заголовок
   *  бывает длинный («Осталось до конца месяца»), и сосед справа его съедает.
   *  Внизу справа места ровно столько, сколько нужно, и так это и нарисовано. */
  corner?: React.ReactNode;
}) {
  if (state === null) return <NotSet onSet={onSet} corner={corner} />;
  const v = limitView(state);
  return (
    <section style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--s-2)" }}>
        <div style={{ flexGrow: 1, minWidth: 0, fontSize: "var(--f-caption)",
                      color: "var(--text-muted)" }}>{v.heading}</div>
        {/* Изменить лимит нужно уметь всегда, а не только пока он не задан. Не
            делаем всю карточку кнопкой: внутри уже живёт капитал, а кнопка в
            кнопке — невалидная разметка и неработающий фокус. */}
        <button type="button" onClick={onSet} aria-label="Изменить лимит"
                style={{ background: "none", border: "none", cursor: "pointer",
                         padding: 8, margin: -8, minWidth: 38, minHeight: 38,
                         display: "flex", alignItems: "center",
                         justifyContent: "center", color: "var(--text-muted)" }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.1 2.1 0 013 3L7.5 18.5l-4 1 1-4z" />
          </svg>
        </button>
      </div>
      <div className="num" style={{ ...hero, color: INK[v.tone] }}>{v.amount}</div>
      <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)", marginTop: 3 }}>
        {v.sub}
      </div>
      <Bar share={v.share} tone={v.tone} text={v.sub} />
      {corner ? <div style={footRow}>{corner}</div> : null}
    </section>
  );
}

function Bar({ share, tone, text }: { share: number; tone: Tone; text?: string }) {
  const fill = Math.min(share, 1) * 100;
  const spill = tone === "over" ? Math.min(share - 1, 1) * 100 : 0;
  const color = tone === "over" ? "var(--danger)" : tone === "near" ? "var(--warn)" : "var(--ok)";
  return (
    <div
      role="progressbar"
      aria-label="Потрачено от лимита"
      aria-valuemin={0}
      aria-valuemax={100}
      // При перерасходе доля больше единицы, и 137 при максимуме 100 — значение
      // вне диапазона: поведение ассистивных технологий на таком не определено.
      aria-valuenow={Math.min(Math.round(share * 100), 100)}
      aria-valuetext={text}
      style={{ height: 6, borderRadius: "var(--r-bar)", background: "var(--surface-3)",
               marginTop: "var(--s-4)", display: "flex", overflow: "hidden" }}
    >
      <div style={{ width: `${fill}%`, background: color }} />
      {/* Доливку перебора отделяем ЗАЗОРОМ, а не прозрачностью: `opacity: 0.7`
          давала к треку 2.36:1 при норме 3:1 для нетекстовых элементов, и на
          светлой теме она сливалась с фоном полосы. Полный цвет даёт 3.56:1. */}
      {spill > 0 && (
        <>
          <div style={{ width: 2, background: "var(--surface)", flexShrink: 0 }} />
          <div style={{ width: `${spill}%`, background: color }} />
        </>
      )}
    </div>
  );
}

/** Лимит не задан. Показываем действие, а не нули: «осталось 0 $» — враньё,
 *  которое диктует поведение человеку, ничего не потратившему (§16.22). */
function NotSet({ onSet, corner }: { onSet: () => void; corner?: React.ReactNode }) {
  return (
    <section style={card}>
      <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)" }}>Лимит не задан</div>
      <div style={{ fontSize: "var(--f-body)", color: "var(--text-muted)",
                    marginTop: "var(--s-2)", lineHeight: 1.6 }}>
        Скажи, сколько готов тратить в месяц — и на каждой трате будет видно, сколько ещё можно.
      </div>
      <button type="button" onClick={onSet} style={primary}>Задать лимит</button>
      {corner ? <div style={footRow}>{corner}</div> : null}
    </section>
  );
}

const footRow: React.CSSProperties = {
  display: "flex", justifyContent: "flex-end", marginTop: "var(--s-3)",
};


const hero: React.CSSProperties = {
  fontSize: "var(--f-t1)",
  fontWeight: 700,
  letterSpacing: "-0.8px",
  marginTop: 5,
  lineHeight: 1.1,
};

const primary: React.CSSProperties = {
  marginTop: "var(--s-4)",
  width: "100%",
  minHeight: "var(--tap)",
  background: "var(--accent)",
  color: "var(--on-accent)",
  border: "none",
  borderRadius: "var(--r-field)",
  padding: 14,
  fontSize: "var(--f-body)",
  fontWeight: 600,
  cursor: "pointer",
  font: "inherit",
};
